/* eslint-disable react/prop-types */
import { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  styled,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { Calendar as BigCalendar } from "react-big-calendar";
import { localizer } from "@/calendarLocalizer";
import { ChevronRightIcon, ChevronLeftIcon } from "lucide-react";

const StyledCalendar = styled(BigCalendar)(({ theme }) => ({
  // Toolbar
  ".rbc-toolbar": {
    margin: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "& .rbc-btn-group button": {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      color: theme.palette.text.primary,
      padding: theme.spacing(0.5, 1),
      margin: theme.spacing(0, 0.5),
      boxShadow: theme.shadows[1],
      transition: "background 0.2s",
      "&:hover": {
        background: theme.palette.action.hover,
      },
    },
    "& .rbc-toolbar-label": {
      fontSize: theme.typography.h6.fontSize,
      fontWeight: theme.typography.h6.fontWeight,
      color: theme.palette.primary.main,
    },
  },
  ".rbc-allday-cell, .rbc-allday-row": {
    display: "none !important",
  },
  ".rbc-time-header": {
    marginTop: 0,
  },

  // Day/Week/Month header cells
  ".rbc-header": {
    background: theme.palette.background.default,
    color: theme.palette.text.secondary,
    padding: theme.spacing(1),
    fontWeight: 600,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  // Date cells
  ".rbc-day-slot .rbc-time-header-cell": {
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  ".rbc-day-bg": {
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },

  // Events
  ".rbc-event": {
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    padding: theme.spacing(0.5),
    fontSize: theme.typography.body2.fontSize,
  },
}));

function eventStyleGetter(event, start, end, isSelected) {
  return {
    style: {
      backgroundColor: isSelected
        ? event.resource?.color || "#1976d2"
        : event.resource?.color || "#2196f3",
      color: "#fff",
      border: "none",
    },
  };
}

function CustomToolbar({ label, onNavigate, onView, views, t }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        m: 2,
      }}
    >
      <Box>
        <IconButton
          aria-label={t("calendar.previous")}
          onClick={() => onNavigate("PREV")}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          aria-label={t("calendar.today")}
          onClick={() => onNavigate("TODAY")}
        >
          {t("calendar.today")}
        </IconButton>
        <IconButton
          aria-label={t("calendar.next")}
          onClick={() => onNavigate("NEXT")}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <Typography variant="h6" color="primary">
        {label}
      </Typography>
      <Box>
        {views.map((v) => (
          <Button
            key={v}
            size="small"
            onClick={() => onView(v)}
            sx={{
              textTransform: "capitalize",
              color: theme.palette.text.secondary,
            }}
          >
            {t(`calendar.${v}`)}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

function CalendarDialog({
  open,
  onClose,
  documents,
  onSelectEvent,
  t,
  i18nLanguage,
  translationPrefix = "myDocuments",
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const calendarMessages = useMemo(
    () => ({
      allDay: t(`${translationPrefix}.calendar.allDay`),
      previous: t(`${translationPrefix}.calendar.previous`),
      next: t(`${translationPrefix}.calendar.next`),
      today: t(`${translationPrefix}.calendar.today`),
      month: t(`${translationPrefix}.calendar.month`),
      week: t(`${translationPrefix}.calendar.week`),
      day: t(`${translationPrefix}.calendar.day`),
      agenda: t(`${translationPrefix}.calendar.agenda`),
      date: t(`${translationPrefix}.calendar.date`),
      time: t(`${translationPrefix}.calendar.time`),
      event: t(`${translationPrefix}.calendar.event`),
      showMore: (count) =>
        t(`${translationPrefix}.calendar.showMore`, { count }),
    }),
    [t, translationPrefix]
  );

  const calendarEvents = useMemo(() => {
    return documents.map((doc) => ({
      title: doc.workflow?.name || doc.title,
      start: new Date(doc.start_timestamp),
      end: new Date(doc.start_timestamp),
      allDay: false,
      resource: doc,
    }));
  }, [documents]);

  return (
    <Dialog
      fullWidth
      maxWidth="xl"
      fullScreen={fullScreen}
      open={open}
      onClose={onClose}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {t(`${translationPrefix}.calendarDialogTitle`, "Calendar View")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ height: "80vh", p: 0 }}>
        <StyledCalendar
          localizer={localizer}
          events={calendarEvents}
          culture={i18nLanguage}
          defaultView="week"
          views={["month", "week", "day"]}
          messages={calendarMessages}
          step={60}
          timeslots={2}
          min={new Date(1970, 1, 1, 0, 0, 0)}
          max={new Date(1970, 1, 1, 23, 59, 59)}
          defaultDate={new Date()}
          style={{ height: "100%" }}
          onSelectEvent={(e) => onSelectEvent(e.resource)}
          eventPropGetter={eventStyleGetter}
          components={{
            toolbar: (toolbarProps) => (
              <CustomToolbar
                {...toolbarProps}
                t={(key) => t(`${translationPrefix}.${key}`)}
              />
            ),
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t(`${translationPrefix}.closeButton`, "Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CalendarDialog;
